import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../hooks/useAuth';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TextField,
  Checkbox,
  IconButton,
  Tooltip,
  Button,
  Typography,
  Pagination,
  Stack,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import SendIcon from '@mui/icons-material/Send';
import ReplayIcon from '@mui/icons-material/Replay';
import DownloadIcon from '@mui/icons-material/Download';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import UndoIcon from '@mui/icons-material/Undo';
import ErrorIcon from '@mui/icons-material/Error';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import {
  DOCUMENT_STATUSES,
  DOCUMENT_STATUS_LABELS,
  STATUS_COLORS,
  MESSAGES,
  STORAGE
} from '../../config/constants';
import { usePermissions } from '../../hooks/usePermissions.jsx';  // Change .js to .jsx
import { PERMISSIONS } from '../../config/permissions';
import { useUserActivity } from '../../hooks/useUserActivity';
import clsx from 'clsx';

const DocumentList = ({
  documents,
  selectedTab,
  onTabChange,
  selectedDocs,
  onSelectDoc,
  onDeleteDoc,
  onUploadFiles,
  onUpdateStatus,
  onDocumentSelect,
  onToggleLock,
  hideUploadButton = false, // Add this new prop with default false
  onUpdateDocuments,
  onUnlockDoc,  // Add this prop
  onDeleteMultipleDocs, // Add this new prop for bulk deletion
  onExportMultipleDocs, // Add prop for bulk export
  onClearSelection, // Add prop to clear selection
  onReprocessDoc, // Voeg de nieuwe prop toe
  userRole, // Voeg de gebruikersrol toe
  onShowSnackbar, // <<< Ontvang de nieuwe prop
  onRevertStatus, // <<< Ontvang de revert prop (nu voor bulk)
}) => {
  const { user } = useContext(AuthContext);
  const [isDragging, setIsDragging] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchVisible, setSearchVisible] = useState(false);
  const { hasPermission } = usePermissions();
  const canUpload = hasPermission(PERMISSIONS.DOCUMENTS.UPLOAD);
  const canDelete = hasPermission(PERMISSIONS.DOCUMENTS.DELETE);  // Add permission check
  const canUnlock = hasPermission(PERMISSIONS.DOCUMENTS.UNLOCK);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const { isActive } = useUserActivity(900000); // 15 minutes
  const [documentJobs, setDocumentJobs] = useState({}); // New state for document jobs
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    const channel = supabase.channel('documents')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'documents',
        },
        async (payload) => {
          if (payload.eventType === 'UPDATE') {
            // Check if this user is trying to lock a document
            if (payload.new.locked_by === user?.id) {
              // Check if user has any other documents locked
              const { data: lockedDocs } = await supabase
                .from('documents')
                .select('id')
                .eq('locked_by', user?.id)
                .neq('id', payload.new.id);

              // If other locked documents found, unlock them
              if (lockedDocs && lockedDocs.length > 0) {
                const { error: unlockError } = await supabase
                  .from('documents')
                  .update({ locked_by: null })
                  .in('id', lockedDocs.map(doc => doc.id));

                if (unlockError) {
                  console.error('Error unlocking previous documents:', unlockError);
                }
              }
            }

            // Fetch the updated document with profile information
            const { data: updatedDocWithProfile } = await supabase
              .from('documents')
              .select(`
                *,
                profiles:locked_by (
                  full_name,
                  email
                )
              `)
              .eq('id', payload.new.id)
              .single();

            // Pass an updater function to onUpdateDocuments
            onUpdateDocuments?.(prevDocs => 
              prevDocs.map(doc =>
                doc.id === payload.new.id
                  ? {
                      ...doc,
                      ...payload.new,
                      isLocked: !!payload.new.locked_by,
                      isLockedByMe: payload.new.locked_by === user?.id,
                      lockedByUser: updatedDocWithProfile?.profiles?.full_name || 
                                  updatedDocWithProfile?.profiles?.email || 
                                  'Onbekend'
                    }
                  : doc
              )
            );
          }
        }
      );

    channel.subscribe();
    
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, onUpdateDocuments]);

  // Effect to unlock documents on user inactivity
  useEffect(() => {
    // If user becomes inactive, attempt to unlock their documents
    if (!isActive && user?.id) {
      const unlockDocs = async () => {
        console.log('User inactive, attempting to unlock documents for user:', user.id);
        const { error } = await supabase
          .from('documents')
          .update({ locked_by: null })
          .eq('locked_by', user.id); // Unlock only docs locked by this user

        if (error) {
          console.error('Error auto-unlocking documents on inactivity:', error);
        } else {
          console.log('Successfully sent unlock request for inactive user.');
          // No direct state update needed here - the channel listener should catch the change
        }
      };
      unlockDocs();
    }
    // Dependencies: isActive and user.id. No need for documents or onUpdateDocuments.
  }, [isActive, user?.id]);

  // Add effect to fetch document jobs for intake documents
  useEffect(() => {
    const fetchDocumentJobs = async () => {
      // Only fetch jobs for documents in intake status
      const intakeDocs = documents.filter(doc => doc.status === DOCUMENT_STATUSES.INTAKE);
      if (intakeDocs.length === 0) return;

      try {
        const { data, error } = await supabase
          .from('document_jobs')
          .select('document_id, status, error_message')
          .in('document_id', intakeDocs.map(doc => doc.id));

        if (error) throw error;

        // Create a map of document_id to job status
        const jobsMap = {};
        data.forEach(job => {
          jobsMap[job.document_id] = {
            status: job.status,
            errorMessage: job.error_message
          };
        });

        setDocumentJobs(jobsMap);
      } catch (error) {
        console.error('Error fetching document jobs:', error);
      }
    };

    fetchDocumentJobs();
  }, [documents]);

  const formatDate = (date) => {
    if (!date) return '-';
    try {
      return new Date(date).toLocaleDateString('nl-NL', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch (e) {
      return '-';
    }
  };

  // Nieuwe functie voor het downloaden van XML
  const handleDownloadXml = async (docId, docName) => {
    console.log(`Attempting to download XML for document ID: ${docId}`);
    try {
      // 1. Zoek de storage path in document_output
      const { data: outputData, error: outputError } = await supabase
        .from('document_output')
        .select('storage_path, file_name')
        .eq('document_id', docId) // Gebruik de nieuwe kolom!
        .limit(1)
        .maybeSingle();

      if (outputError) {
        throw new Error(`Fout bij opzoeken export data: ${outputError.message}`);
      }

      if (!outputData || !outputData.storage_path) {
        throw new Error(`Geen geëxporteerd XML-bestand gevonden voor dit document.`);
      }

      const storagePath = outputData.storage_path;
      const fileName = outputData.file_name || `${docName || docId}_export.xml`; // Fallback filename
      const bucketName = 'xml-output'; // Bucket naam

      console.log(`Found storage path: ${storagePath}, filename: ${fileName}. Creating signed URL from bucket: ${bucketName}...`);

      // 2. Genereer een signed URL voor het bestand
      const expiresIn = 60; // URL is 60 seconden geldig
      const { data: signedUrlData, error: signedUrlError } = await supabase.storage
        .from(bucketName)
        .createSignedUrl(storagePath, expiresIn);

      if (signedUrlError) {
        throw new Error(`Fout bij genereren download link: ${signedUrlError.message}`);
      }

      if (!signedUrlData?.signedUrl) {
        throw new Error('Download mislukt: kon geen geldige download link genereren.');
      }

      console.log(`Signed URL generated successfully. Fetching blob content...`);

      // 3. Fetch the file content using the signed URL
      const response = await fetch(signedUrlData.signedUrl);
      if (!response.ok) {
        throw new Error(`Fout bij ophalen XML-bestand: ${response.statusText}`);
      }
      const blob = await response.blob();

      console.log(`Blob fetched successfully, size: ${blob.size} bytes. Creating download link...`);

      // 4. Create an object URL for the blob and trigger download
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // 5. Clean up the object URL
      URL.revokeObjectURL(blobUrl);

      // Optioneel: Toon succesmelding
      onShowSnackbar?.({ open: true, message: `${fileName} wordt gedownload.`, severity: 'success' });

    } catch (error) {
      console.error('Download XML error:', error);
      // Toon foutmelding (via Snackbar of ErrorPopup?)
      alert(`Kon XML niet downloaden: ${error.message}`); // Gebruik alert voor nu, of integreer met Snackbar/ErrorPopup
    }
  };

  // Bijgewerkte functie voor bulk downloaden via backend ZIP functie
  const handleBulkDownloadXml = async (docIds) => {
    if (!docIds || docIds.length === 0) {
      // Gebruik de prop voor feedback
      onShowSnackbar?.({ open: true, message: 'Geen documenten geselecteerd.', severity: 'warning' }); 
      return;
    }

    const totalCount = docIds.length;
    // Gebruik de prop voor feedback
    onShowSnackbar?.({ open: true, message: `Start genereren ZIP voor ${totalCount} document(en)...`, severity: 'info' }); 

    try {
      console.log(`Invoking zip-exported-documents for document IDs: ${docIds.join(', ')}`);
      const { data: zipData, error: functionError } = await supabase.functions.invoke('zip-exported-documents', {
        body: { documentIds: docIds },
      });

      if (functionError || (zipData && !zipData.success)) {
        const errorMessage = functionError?.message || zipData?.error || 'Onbekende fout bij genereren ZIP.';
        console.error('zip-exported-documents function error:', errorMessage, functionError, zipData);
        throw new Error(errorMessage); // Gooi error om in catch af te handelen
      }

      // Succes! Start download via signed URL
      console.log('zip-exported-documents success:', zipData);
      if (zipData.signedUrl) {
        // Gebruik de prop voor feedback
        onShowSnackbar?.({ open: true, message: `ZIP bestand (${zipData.fileName || 'export.zip'}, ${zipData.filesIncluded}/${totalCount} bestanden) wordt gedownload.`, severity: 'success' }); 
        // Open de URL in een nieuw tabblad of hetzelfde venster
        window.open(zipData.signedUrl, '_blank'); // _blank opent in nieuw tabblad
        // Of: window.location.href = zipData.signedUrl; // Leidt huidige venster om
      } else {
        throw new Error('Functie succesvol, maar geen download URL ontvangen.');
      }

      // Optioneel: Wis selectie na starten download
      // onClearSelection?.();

    } catch (error) {
      console.error('Bulk download (ZIP) error:', error);
      // Gebruik de prop voor feedback
      onShowSnackbar?.({ open: true, message: `Kon ZIP bestand niet genereren of downloaden: ${error.message}`, severity: 'error' }); 
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    onUploadFiles(files);
  };

  // Filter documents based on selected tab and search term
  const statusKeys = Object.keys(DOCUMENT_STATUS_LABELS);
  const filteredDocuments = documents.filter(doc => 
    doc.status === statusKeys[selectedTab] && 
    doc.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate pagination
  const totalPages = Math.ceil(filteredDocuments.length / itemsPerPage);
  const sortedDocuments = [...filteredDocuments].sort((a, b) => {
    let aValue, bValue;
    switch (sortBy) {
      case 'name':
        aValue = a.name || '';
        bValue = b.name || '';
        break;
      case 'created_at':
      default:
        aValue = a.received_at || a.created_at;
        bValue = b.received_at || b.created_at;
        break;
    }
    if (sortBy === 'created_at') {
      return sortDirection === 'asc'
        ? new Date(aValue) - new Date(bValue)
        : new Date(bValue) - new Date(aValue);
    } else {
      return sortDirection === 'asc'
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    }
  });
  const paginatedDocuments = sortedDocuments.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  // Calculate selected count for the current page
  const numSelected = paginatedDocuments.filter(doc => selectedDocs.includes(doc.id)).length;
  const rowCount = paginatedDocuments.length;

  // Handler for the "select all" checkbox
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      // Select all documents on the current page
      const newSelecteds = paginatedDocuments.map((n) => n.id);
      newSelecteds.forEach(id => {
        // Only call onSelectDoc if the doc is not already selected
        // This prevents unnecessary state updates in the parent
        if (!selectedDocs.includes(id)) {
          onSelectDoc(id, true);
        }
      });
    } else {
      // Deselect all documents on the current page
      paginatedDocuments.forEach(doc => {
        // Only call onSelectDoc if the doc is currently selected
        if (selectedDocs.includes(doc.id)) {
          onSelectDoc(doc.id, false);
        }
      });
    }
  };

  // Reset page and clear selection when tab or search changes
  useEffect(() => {
    setPage(1);
    onClearSelection?.(); // Clear selection
  }, [selectedTab, searchTerm, onClearSelection]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    onClearSelection?.(); // Clear selection on page change
  };

  const handleRowsPerPageChange = (event) => {
    setItemsPerPage(event.target.value);
    setPage(1); // Reset to first page
    onClearSelection?.(); // Clear selection on rows per page change
  };

  const getStatusColor = (status) => {
    switch (status) {
      case DOCUMENT_STATUSES.INTAKE: return 'info';
      case DOCUMENT_STATUSES.TO_REVIEW: return 'warning';
      case DOCUMENT_STATUSES.REVIEWED: return 'success';
      case DOCUMENT_STATUSES.EXPORTED: return 'primary';
      default: return 'default';
    }
  };

  // New function to handle bulk deletion
  const handleDeleteSelected = () => {
    if (selectedDocs.length > 0) {
      onDeleteMultipleDocs?.(selectedDocs);
    }
  };

  // New function to handle bulk export
  const handleExportSelected = () => {
    if (selectedDocs.length > 0) {
      onExportMultipleDocs?.(selectedDocs);
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (active, direction) => {
    if (!active) return <ArrowDropDownIcon sx={{ opacity: 0.3, ml: 0.5, fontSize: 20 }} />;
    return direction === 'asc'
      ? <ArrowDropUpIcon sx={{ color: 'primary.main', ml: 0.5, fontSize: 20 }} />
      : <ArrowDropDownIcon sx={{ color: 'primary.main', ml: 0.5, fontSize: 20 }} />;
  };

  return (
    <Box
      onDragOver={canUpload ? handleDragOver : undefined}
      onDragLeave={canUpload ? handleDragLeave : undefined}
      onDrop={canUpload ? handleDrop : undefined}
      sx={{
        position: 'relative',
        minHeight: '100%',
        paddingBottom: '80px' // Add padding to ensure content doesn't get hidden behind the upload button
      }}
    >
      {/* Upload button - Only show if user has upload permission */}
      {canUpload && (
        <Button
          variant="contained"
          startIcon={<CloudUploadIcon />}
          sx={theme => ({
            position: 'fixed',
            bottom: 32,
            left: 282, // 250px sidebar + 32px margin
            zIndex: 1000,
            bgcolor: STATUS_COLORS[DOCUMENT_STATUSES.INTAKE],
            boxShadow: 3, // Add shadow to make it stand out
            // Hide on mobile (screens smaller than md breakpoint)
            display: { xs: 'none', md: 'flex' }, 
          })}
          component="label"
        >
          Upload Bestanden
          <input
            id="document-upload-input"
            type="file"
            hidden
            multiple
            accept={STORAGE.ALLOWED_TYPES.join(',')}
            onChange={(e) => onUploadFiles(Array.from(e.target.files))}
          />
        </Button>
      )}

      {/* Drag overlay - Only show if user has upload permission */}
      {canUpload && isDragging && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(25, 118, 210, 0.1)',
            border: '2px dashed #1976d2',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <Typography variant="h6" color="primary">
            Sleep bestanden hier om te uploaden
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Alleen PDF en XML bestanden zijn toegestaan
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Tabs 
          value={selectedTab} 
          onChange={onTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          {Object.entries(DOCUMENT_STATUS_LABELS).map(([status, label], index) => (
            <Tab 
              key={status}
              label={`${label} (${documents.filter(d => d.status === status).length})`}
            />
          ))}
        </Tabs>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Add bulk export button - Only show on Reviewed tab when docs selected */}
          {statusKeys[selectedTab] === DOCUMENT_STATUSES.REVIEWED && selectedDocs.length > 0 && (
            <Tooltip title={`Exporteer geselecteerd (${selectedDocs.length})`} arrow>
              <Button
                variant="contained"
                color="primary"
                onClick={handleExportSelected}
                size="small"
                sx={{ minWidth: 0, padding: '4px' }} // Compact maken
              >
                <SendIcon sx={{ fontSize: 18 }} />
              </Button>
            </Tooltip>
          )}
          {/* Add bulk delete button - Hide in intake tab unless user is admin, and hide for exported docs unless admin */}
          {canDelete && selectedDocs.length > 0 && (statusKeys[selectedTab] !== DOCUMENT_STATUSES.INTAKE || userRole === 'admin') && (statusKeys[selectedTab] !== DOCUMENT_STATUSES.EXPORTED || userRole === 'admin') && (
            <Tooltip title={`Verwijder geselecteerd (${selectedDocs.length})`} arrow>
              <Button
                variant="contained"
                color="error"
                onClick={handleDeleteSelected}
                size="small"
                sx={{ minWidth: 0, padding: '4px' }} // Compact maken
              >
                <DeleteIcon sx={{ fontSize: 18 }} />
              </Button>
            </Tooltip>
          )}
          {/* Add bulk download button - Only show on Exported tab when docs selected */}
          {statusKeys[selectedTab] === DOCUMENT_STATUSES.EXPORTED && selectedDocs.length > 0 && (
            <Tooltip title={`Download selectie (${selectedDocs.length})`} arrow>
              <Button
                variant="contained"
                color="primary" // Kleur naar primary
                onClick={() => handleBulkDownloadXml(selectedDocs)}
                size="small"
                sx={{ minWidth: 0, padding: '4px' }} // Compact maken
              >
                <FileDownloadIcon sx={{ fontSize: 18 }} />
              </Button>
            </Tooltip>
          )}
          {/* Add bulk revert status button - Only show on Exported tab when docs selected */}
          {statusKeys[selectedTab] === DOCUMENT_STATUSES.EXPORTED && selectedDocs.length > 0 && (
            <Tooltip title={`Zet selectie terug naar 'Beoordeeld' (${selectedDocs.length})`} arrow>
              <Button
                variant="contained"
                color="warning" // Gebruik een andere kleur, bv. warning
                onClick={() => onRevertStatus?.(selectedDocs)}
                size="small"
                sx={{ minWidth: 0, padding: '4px' }} // Compact maken
              >
                <UndoIcon sx={{ fontSize: 18 }} />
              </Button>
            </Tooltip>
          )}
          {searchVisible ? (
            <>
              <TextField
                id="document-search-field"
                size="small"
                placeholder="Zoeken..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
                sx={{ mr: 1 }}
              />
              <IconButton
                size="small"
                onClick={() => {
                  setSearchVisible(false);
                  setSearchTerm('');
                }}
              >
                <CloseIcon />
              </IconButton>
            </>
          ) : (
            <Tooltip title="Zoeken" arrow>
              <IconButton size="small" onClick={() => setSearchVisible(true)}>
                <SearchIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  id="select-all-documents-checkbox"
                  indeterminate={numSelected > 0 && numSelected < rowCount}
                  checked={rowCount > 0 && numSelected === rowCount}
                  onChange={handleSelectAllClick}
                  inputProps={{ 'aria-label': 'select all documents on this page' }}
                />
              </TableCell>
              <TableCell
                sx={{ cursor: 'pointer', fontWeight: 600, userSelect: 'none', bgcolor: 'background.paper', borderBottom: 2, borderColor: 'divider', transition: 'background 0.2s', '&:hover': { bgcolor: 'action.hover' }, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}
                onClick={() => handleSort('name')}
                aria-sort={sortBy === 'name' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
                title="Sorteer op naam"
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Document naam
                  {getSortIcon(sortBy === 'name', sortDirection)}
                </Box>
              </TableCell>
              <TableCell
                sx={{ cursor: 'pointer', fontWeight: 600, userSelect: 'none', bgcolor: 'background.paper', borderBottom: 2, borderColor: 'divider', transition: 'background 0.2s', '&:hover': { bgcolor: 'action.hover' }, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 }}
                onClick={() => handleSort('created_at')}
                aria-sort={sortBy === 'created_at' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
                title="Sorteer op ontvangen op"
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Ontvangen op
                  {getSortIcon(sortBy === 'created_at', sortDirection)}
                </Box>
              </TableCell>
              <TableCell align="right">Acties</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedDocuments.map((doc) => {
              const isIntakeTab = statusKeys[selectedTab] === DOCUMENT_STATUSES.INTAKE;
              const isExportedTab = statusKeys[selectedTab] === DOCUMENT_STATUSES.EXPORTED;
              const isDocumentExported = !!doc.exported_at;
              const isDisabled = (doc.isLocked && !doc.isLockedByMe) || isIntakeTab;
              // Disable row click specifically for exported tab to prevent accidental opening
              const isRowClickDisabled = isDisabled;

              return (
                <TableRow 
                  key={doc.id} 
                  hover={!isDisabled}
                  onClick={(e) => {
                    // Only open document if not clicking on the checkbox or its cell, AND row click is not disabled
                    if (!e.target.closest('td[data-checkbox-cell="true"]') && !isRowClickDisabled) {
                      isDisabled ? null : onDocumentSelect(doc);
                    }
                  }}
                  sx={{
                    cursor: isRowClickDisabled ? 'not-allowed' : 'pointer', // Pas cursor aan op basis van of rij klikbaar is
                    backgroundColor: theme => {
                      if (doc.isLocked && !doc.isLockedByMe) {
                        return 'action.disabledBackground';
                      }
                      return (isIntakeTab) ? 'action.selected' : 'inherit';
                    },
                    opacity: isDisabled ? 0.7 : 1,
                    '& td': {
                      color: isDisabled ? 'text.disabled' : 'inherit',
                    },
                    '&:hover': {
                      backgroundColor: isDisabled ? 
                        'action.disabledBackground' : 
                        'action.hover',
                    }
                  }}
                >
                  <TableCell padding="checkbox" data-checkbox-cell="true">
                    <Checkbox
                      id={`select-doc-checkbox-${doc.id}`}
                      checked={selectedDocs.includes(doc.id)}
                      onChange={(e) => {
                        e.stopPropagation(); // Prevent row click
                        onSelectDoc(doc.id, e.target.checked);
                      }}
                      onClick={(e) => e.stopPropagation()} // Ensure checkbox click doesn't trigger row click
                      // Disable checkbox if row is disabled (Intake tab or locked by other) OR if user cannot unlock
                      disabled={isDisabled || (doc.isLocked && !doc.isLockedByMe && !canUnlock)}
                    />
                  </TableCell>
                  <TableCell
                    sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}
                    title={doc.name}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {doc.name}
                      {isIntakeTab && (
                        <>
                          {documentJobs[doc.id]?.status === 'failed' ? (
                            <Tooltip title={documentJobs[doc.id]?.errorMessage || 'Verwerking mislukt'}>
                              <ErrorIcon 
                                sx={{ 
                                  ml: 1, 
                                  color: 'error.main',
                                  fontSize: '1.2rem'
                                }} 
                              />
                            </Tooltip>
                          ) : (
                            <CircularProgress 
                              size={16} 
                              sx={{ ml: 1, color: 'primary.light' }} 
                            />
                          )}
                        </>
                      )}
                      {doc.isLocked && !doc.isLockedByMe && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          (In gebruik door {doc.lockedByUser})
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{formatDate(doc.received_at || doc.created_at)}</TableCell>
                  <TableCell padding="checkbox" align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {/* Bekijk/Bewerk Knop (Niet tonen in Exported tab) */}
                    {!isExportedTab && (
                      <Tooltip title={doc.isLocked && !doc.isLockedByMe ? `Vergrendeld door ${doc.lockedByUser}` : "Bekijk/Bewerk"} arrow>
                        {/* Span is nodig voor Tooltip op disabled button */}
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => onDocumentSelect(doc)}
                            disabled={isDisabled} // Gebruik algemene isDisabled
                          >
                            <SearchIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}

                    {/* Download Knop (Alleen tonen in Exported tab) */}
                    {isExportedTab && (
                       <Tooltip title="Download geëxporteerde XML" arrow>
                         <IconButton
                           size="small"
                           onClick={(e) => {
                             e.stopPropagation(); // Voorkom rij selectie
                             handleDownloadXml(doc.id, doc.name);
                           }}
                           // Eventueel disablen als er geen export record is? (vereist extra check)
                         >
                           <DownloadIcon fontSize="small" />
                         </IconButton>
                       </Tooltip>
                    )}

                    {/* Herverwerk Knop (alleen voor admins, niet in Exported tab) */}
                    {userRole === 'admin' && !isExportedTab && (
                       <Tooltip title="Herverwerk" arrow>
                         <IconButton
                           size="small"
                           onClick={(e) => {
                             e.stopPropagation(); // Voorkom rij selectie
                             onReprocessDoc(doc.id);
                           }}
                           // Optioneel: disable als het document al bezig is met herverwerken
                           // disabled={doc.status === 'reprocessing_queued'}
                         >
                           <ReplayIcon fontSize="small" />
                         </IconButton>
                       </Tooltip>
                    )}

                    {/* Ontgrendel Knop (alleen als vergrendeld & gebruiker heeft permissie) */}
                    {doc.isLocked && canUnlock && (
                      <Tooltip title="Ontgrendel" arrow>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation(); // Voorkom rij selectie
                            onUnlockDoc(doc.id);
                          }}
                        >
                          <LockOpenIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {/* Verwijder Knop (alleen als gebruiker permissie heeft en niet in intake tab, tenzij admin, en niet geëxporteerd tenzij admin) */}
                    {canDelete && (!isIntakeTab || userRole === 'admin') && (!isDocumentExported || userRole === 'admin') && (
                      <Tooltip title="Verwijder" arrow>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation(); // Voorkom rij selectie
                            onDeleteDoc(doc.id);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add pagination controls */}
      <Stack
        spacing={2}
        direction="row"
        alignItems="center"
        justifyContent="center"
        sx={{ mt: 2, mb: 2 }}
      >
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            id="items-per-page-select"
            value={itemsPerPage}
            onChange={handleRowsPerPageChange}
            displayEmpty
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary">
          Pagina {page} van {totalPages || 1}
        </Typography>
        <Pagination
          count={totalPages || 1}
          page={page}
          onChange={handlePageChange}
          color="primary"
          showFirstButton
          showLastButton
        />
      </Stack>
    </Box>
  );
};

export default DocumentList;
